import { useState } from 'react';
import { Download, Share2, Printer, CheckCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { date } from '../../utils/formatters';

const FUEL_LABELS = {
  empty: 'Reserva',
  quarter: '1/4',
  half: '1/2',
  three_quarters: '3/4',
  full: 'Cheio',
};

export default function CheckinReceipt({ checkin, os, cliente, veiculo, onClose }) {
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;

      doc.setFontSize(20);
      doc.setTextColor(249, 115, 22);
      doc.text('MecSync - Comprovante de Check-in', pageWidth / 2, y, { align: 'center' });
      y += 15;

      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Data: ${date(checkin.completed_at)}`, pageWidth / 2, y, { align: 'center' });
      y += 20;

      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Ordem de Serviço', 20, y);
      y += 8;
      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(`OS #${os.numero}`, 20, y);
      y += 6;
      doc.text(`Status: ${os.status}`, 20, y);
      y += 15;

      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Cliente', 20, y);
      y += 8;
      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(cliente.nome, 20, y);
      y += 6;
      if (cliente.telefone) {
        doc.text(`Telefone: ${cliente.telefone}`, 20, y);
        y += 6;
      }
      y += 15;

      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Veículo', 20, y);
      y += 8;
      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(`${veiculo.marca} ${veiculo.modelo}`, 20, y);
      y += 6;
      doc.text(`Placa: ${veiculo.placa}`, 20, y);
      y += 6;
      doc.text(`Ano: ${veiculo.ano}`, 20, y);
      y += 15;

      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Check-in Digital', 20, y);
      y += 8;
      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(`Quilometragem: ${checkin.odometer_value} km`, 20, y);
      y += 6;
      doc.text(`Combustível: ${FUEL_LABELS[checkin.fuel_level] || checkin.fuel_level}`, 20, y);
      y += 15;

      if (checkin.exterior_condition) {
        doc.setFontSize(11);
        doc.text(`Condição externa: ${checkin.exterior_condition}`, 20, y);
        y += 8;
      }

      if (checkin.dashboard_notes) {
        doc.setFontSize(11);
        doc.text(`Observações painel: ${checkin.dashboard_notes}`, 20, y);
        y += 8;
      }

      if (checkin.customer_notes) {
        doc.setFontSize(11);
        doc.text(`Observações cliente: ${checkin.customer_notes}`, 20, y);
        y += 8;
      }

      y += 15;
      doc.setFontSize(10);
      doc.setTextColor(150);
      doc.text('Este comprovante atesta o estado do veículo na entrada.', 20, y);
      y += 6;
      doc.text('MecSync - Central Inteligente da Oficina', 20, y);

      doc.save(`checkin-os-${os.numero}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const shareReceipt = async () => {
    const text = `Comprovante de Check-in MecSync\n\nOS: #${os.numero}\nCliente: ${cliente.nome}\nVeículo: ${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}\nQuilometragem: ${checkin.odometer_value} km\nCombustível: ${FUEL_LABELS[checkin.fuel_level] || checkin.fuel_level}\nData: ${date(checkin.completed_at)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Comprovante de Check-in MecSync',
          text,
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    } else {
      await navigator.clipboard.writeText(text);
    }
  };

  return (
    <Card>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle size={20} className="text-green-400" />
            Check-in Realizado
          </h3>
          <p className="text-sm text-zinc-400">Comprovante digital gerado com sucesso</p>
        </div>
        <Badge className="bg-green-500/10 text-green-400">Concluído</Badge>
      </div>

      <div className="space-y-4 rounded-lg border border-white/10 bg-surface2 p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Ordem de Serviço</p>
            <p className="font-semibold">#{os.numero}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Data</p>
            <p className="font-semibold">{date(checkin.completed_at)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Cliente</p>
            <p className="font-semibold">{cliente.nome}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Veículo</p>
            <p className="font-semibold">{veiculo.marca} {veiculo.modelo} - {veiculo.placa}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Quilometragem</p>
            <p className="font-semibold">{checkin.odometer_value} km</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Combustível</p>
            <p className="font-semibold">{FUEL_LABELS[checkin.fuel_level] || checkin.fuel_level}</p>
          </div>
        </div>

        {checkin.exterior_condition && (
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Condição Externa</p>
            <p className="text-sm">{checkin.exterior_condition}</p>
          </div>
        )}

        {checkin.dashboard_notes && (
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Observações do Painel</p>
            <p className="text-sm">{checkin.dashboard_notes}</p>
          </div>
        )}

        {checkin.customer_notes && (
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Observações do Cliente</p>
            <p className="text-sm">{checkin.customer_notes}</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={generatePDF} disabled={generatingPdf}>
          <Download size={16} className="mr-2" />
          {generatingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
        </Button>
        <Button variant="ghost" onClick={shareReceipt}>
          <Share2 size={16} className="mr-2" />
          Compartilhar
        </Button>
        <Button variant="ghost" onClick={window.print}>
          <Printer size={16} className="mr-2" />
          Imprimir
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </Card>
  );
}
